import { OrganizationMarketingHeader } from '@features/marketing/components/organization-marketing-header'
import { routes } from '~/features/routes'
import { Button, CheckIcon, Modal, IconTip } from '@st/theme'
import { inflect } from '@st/util/inflect'
import { ReactNode, useState } from 'react'
import { match } from '@st/pattern'
import { DiscountBanner, PurchaseCreditsDialog } from '~/features/billing'
import { STAppHeaderNav } from '../st-organizations/st-app-header-nav'
import { useOrganizationBilling } from './hooks'
import { STPricingFAQList } from './st-pricing-faq-list'
import { useProcess } from '~/store'
import { routeModule } from '~/features/route-module'
import { OrganizationBillingLoadFailed } from './organization-billing-load-failed'

type OrganizationPlansPageAction =
  | { type: 'showPurchaseCreditsDialog' }
  | { type: 'bookCall' }
  | { type: 'signUp' }

export function OrganizationPlansPage() {
  const [state, send] = useOrganizationBilling()
  const route = useProcess('route')

  const [action, setAction] = useState<OrganizationPlansPageAction | undefined>()

  function onAction(action: OrganizationPlansPageAction) {
    switch (action.type) {
      case 'showPurchaseCreditsDialog':
        setAction(action)
        break
      case 'bookCall':
        route.send({ type: 'openUrl', url: 'https://calendly.com/stanford-tax/intro' })
        break
      case 'signUp':
        route.send({ type: 'navigate', pathname: '/sign-up' })
        break
    }
  }

  return (
    <div>
      <STAppHeaderNav />

      <DiscountBanner />

      <div className="flex flex-col items-center px-16 py-8">
        <h1 className="text-center text-4xl text-gray-800">Pricing</h1>

        {match(state)
          .with({ status: 'idle' }, () => null)
          .with({ status: 'loadingOrganization' }, () => null)
          .with({ status: 'loadingBilling' }, () => null)
          .with({ status: 'loadFailed' }, () => (
            <OrganizationBillingLoadFailed onRetry={() => send({ type: 'retryBillingLoad' })} />
          ))
          .with({ status: 'loaded' }, ({ billingState }) => {
            return (
              <>
                <div className="text-md text-center text-gray-600">
                  {`You currently have ${billingState.creditsStatement.currentBalance} ${inflect(
                    billingState.creditsStatement.currentBalance,
                    'credit'
                  )} in your account.`}
                </div>

                <OrganizationPlansList onAction={onAction} isLoggedIn={true} />

                <h1 className="mb-6 mt-10 text-center text-4xl text-gray-800">
                  Frequently Asked Questions
                </h1>

                <STPricingFAQList />

                <BookConsultationNotice />
              </>
            )
          })
          .otherwise(() => null)}
      </div>

      {match(action)
        .with({ type: 'showPurchaseCreditsDialog' }, () => {
          return (
            <Modal isOpen={true}>
              <PurchaseCreditsDialog
                onClose={() => setAction(undefined)}
              />
            </Modal>
          )
        })
        .otherwise(() => null)}
    </div>
  )
}

export function OrganizationLoggedOutPlansPage() {
  const route = useProcess('route')

  function onAction(action: OrganizationPlansPageAction) {
    switch (action.type) {
      case 'bookCall':
        route.send({ type: 'openUrl', url: 'https://calendly.com/stanford-tax/intro' })
        break
      case 'signUp':
        route.send({ type: 'navigate', pathname: routes.toPath({ name: 'sign_up' })! })
        break
    }
  }

  return (
    <div>
      <OrganizationMarketingHeader />
      <DiscountBanner />

      <div className="flex flex-col items-center px-16 py-8">
        <h1 className="text-center text-4xl text-gray-800">Plans</h1>
        <OrganizationPlansList onAction={onAction} isLoggedIn={false} />

        <h1 className="mb-6 mt-16 text-center text-4xl text-gray-800">
          Frequently Asked Questions
        </h1>

        <STPricingFAQList />

        <BookConsultationNotice />
      </div>
    </div>
  )
}

function OrganizationPlansList({
  onAction,
  isLoggedIn
}: {
  onAction: (action: OrganizationPlansPageAction) => void
  isLoggedIn: boolean
}) {
  return (
    <div className="isolate mx-auto mt-10 flex flex-col items-center gap-8 md:max-w-lg lg:max-w-5xl lg:flex-row lg:items-stretch lg:justify-center xl:max-w-6xl">
      <PlanCard>
        <PlanHeader name="Free" description="Give StanfordTax a test drive with five returns." />
        <PlanPriceDisplay price="$0" />
        {!isLoggedIn && (
          <Button
            className="my-6 w-full"
            variant="primary"
            onClick={() => onAction({ type: 'signUp' })}
          >
            Get started
          </Button>
        )}
        <PlanFeatureList>
          <PlanFeature>Unlimited firm users, up to 5 returns</PlanFeature>
          <PlanSeparator />
          <PlanFeature>Client intake</PlanFeature>
          <PlanFeature>Workpapers</PlanFeature>
          <PlanFeature>Tax Return Delivery</PlanFeature>
          <PlanFeature>1040, 1120, 1120s, 1065</PlanFeature>
        </PlanFeatureList>
      </PlanCard>

      <PlanCard>
        <PlanHeader
          name="Premium"
          description="Our most popular plan, designed for firms of all sizes."
        />
        <PlanPriceDisplay price="$20" priceSuffix="/return" />
        <Button
          className="my-6 w-full"
          variant="primary"
          onClick={() =>
            onAction(isLoggedIn ? { type: 'showPurchaseCreditsDialog' } : { type: 'signUp' })
          }
        >
          Purchase credits
        </Button>
        <PlanFeatureList>
          <PlanFeature>Unlimited firm users</PlanFeature>
          <PlanSeparator />
          <PlanFeature>Client intake</PlanFeature>
          <PlanFeature>Workpapers</PlanFeature>
          <PlanFeature>Tax Return Delivery</PlanFeature>
          <PlanFeature>1040, 1120, 1120s, 1065</PlanFeature>
          <PlanSeparator />
          <PlanFeature>Email and live chat support</PlanFeature>
          <PlanFeature>24-hour support response time</PlanFeature>
          <PlanSeparator />
          <PlanFeature>No minimums</PlanFeature>
          <PlanFeature>No implementation fees</PlanFeature>
          <PlanFeature>Credits never expire</PlanFeature>
          <PlanFeature>
            Only pay for binders you actually use
            <IconTip body="Only binders with one or more documents uploaded will count towards usage." />
          </PlanFeature>
        </PlanFeatureList>
      </PlanCard>

      <PlanCard>
        <PlanHeader
          name="Enterprise"
          description="Our most advanced plan, designed for large firms looking for custom integrations."
        />
        <PlanPriceDisplay price="$30+" priceSuffix="/return" />
        <Button
          className="my-6 w-full"
          variant="primary"
          onClick={() => onAction({ type: 'bookCall' })}
        >
          Talk to sales
        </Button>
        <PlanFeatureList>
          <PlanFeature>Unlimited firm users</PlanFeature>
          <PlanSeparator />
          <PlanFeature>Client intake</PlanFeature>
          <PlanFeature>Workpapers</PlanFeature>
          <PlanFeature>Tax Return Delivery</PlanFeature>
          <PlanFeature>1040, 1120, 1120s, 1065</PlanFeature>
          <PlanSeparator />
          <PlanFeature>Live chat and phone support</PlanFeature>
          <PlanFeature>1-hour support and response time</PlanFeature>
          <PlanSeparator />
          <PlanFeature>Minimum of 5,000 clients</PlanFeature>
          <PlanFeature>Implementation starts at $10,000</PlanFeature>
          <PlanFeature>Credits never expire</PlanFeature>

          <PlanSeparator />
          <PlanFeature>Custom integrations</PlanFeature>
        </PlanFeatureList>
      </PlanCard>
    </div>
  )
}

function PlanCard({ children }: { children: ReactNode }) {
  return <div className="w-full rounded-3xl p-8 ring-1 ring-gray-200 lg:flex-1">{children}</div>
}

function PlanHeader({ name, description }: { name: string; description: string }) {
  return (
    <>
      <h3 className="text-lg/8 font-semibold text-gray-900">{name}</h3>
      <p className="mt-4 h-12 text-base text-gray-600">{description}</p>
    </>
  )
}

function PlanPriceDisplay({ price, priceSuffix }: { price?: string; priceSuffix?: string }) {
  return (
    <p className="mt-6 flex h-12 items-baseline gap-x-1">
      {price && (
        <span className="text-4xl font-semibold tracking-tight text-gray-900">{price}</span>
      )}
      {price && price !== '$0' && priceSuffix && (
        <span className="text-base font-semibold text-gray-600">{priceSuffix}</span>
      )}
    </p>
  )
}

function PlanFeatureList({ children }: { children: ReactNode }) {
  return (
    <ul role="list" className="mt-8 space-y-3 text-sm/6 text-gray-600">
      {children}
    </ul>
  )
}

function PlanFeature({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-x-3 text-base">
      <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
      {children}
    </li>
  )
}

function PlanSeparator() {
  return (
    <li className="flex gap-x-3 text-base">
      <hr className="my-2 w-full border-t bg-stone-200" />
    </li>
  )
}

function BookConsultationNotice() {
  return (
    <div className="mt-8 text-base">
      {`Didn't find the answer to your question? `}
      <a
        className="text-base text-blue-500"
        target="_blank"
        rel="noopener noreferrer"
        href="https://stanfordtax.webinargeek.com/stanfordtax-demo-1"
      >
        Register for a demo
      </a>
    </div>
  )
}
